import { useEffect, useState } from "react";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";

type CurrencyResponse = components["schemas"]["CurrencyResponse"];

export interface CreateProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** Pre-selected currency (e.g. the invoice's); takes priority over the organisation default. */
  defaultCurrency?: string;
}

export function CreateProductDialog({
  open,
  onOpenChange,
  onCreated,
  defaultCurrency,
}: CreateProductDialogProps) {
  const api = useDolfinClient();
  const { organisationId } = useDolfinAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [currency, setCurrency] = useState("");
  const [type, setType] = useState<"Product" | "Service">("Product");
  const [currencies, setCurrencies] = useState<CurrencyResponse[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function load() {
      const [currenciesRes, organisationRes] = await Promise.all([
        api.GET("/v1/currencies/available"),
        organisationId
          ? api.GET("/v1/organisations/{id}", { params: { path: { id: organisationId } } })
          : Promise.resolve({ data: null }),
      ]);
      if (cancelled) return;
      setCurrencies((currenciesRes.data as CurrencyResponse[]) ?? []);
      const orgDefault = (organisationRes.data as any)?.defaultCurrency as string | null | undefined;
      setCurrency((prev) => prev || defaultCurrency || orgDefault || "");
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, organisationId, defaultCurrency]);

  function resetForm() {
    setName("");
    setDescription("");
    setUnitPrice("");
    setCurrency("");
    setType("Product");
    setSubmitting(false);
    setError(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!currency) {
      setError("Currency is required.");
      return;
    }

    // Required by the API (Product.InvalidPrice). Test for empty, not falsy: zero is a valid price, and £0 should be stated.
    if (unitPrice.trim() === "") {
      setError("Unit price is required. Enter 0 for a free item.");
      return;
    }

    const priceNum = Number(unitPrice);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Unit price must be zero or more.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.POST("/v1/products", {
      body: {
        name: name.trim(),
        description: description.trim() || null,
        unitPrice: priceNum,
        currency,
        type,
      },
    });

    if (apiError) {
      const message = apiErrorMessage(apiError, "Failed to create product.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Product</DialogTitle>
            <DialogDescription>
              Add a new product or service to your catalogue.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product-name">Name</Label>
              <Input
                id="product-name"
                placeholder="e.g. Web Development"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product-description">Description</Label>
              <Textarea
                id="product-description"
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="product-price">Unit Price</Label>
                <Input
                  id="product-price"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.id} value={c.code}>
                        {c.code} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as "Product" | "Service")}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Product">Product</SelectItem>
                  <SelectItem value="Service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
